// src/pages/admin/ViewBlogs.jsx
import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  LinearProgress,
  Avatar,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import {
  getAllBlogsAdmin,
  deleteBlog,
  updateBlog,
  getAllBlogCategoriesAdmin,
} from "../../services/api";
import { useNavigate } from "react-router-dom";

/**
 * Note: this component relies on environment variable:
 *   REACT_APP_API_URL=http://localhost:4000
 * (for CRA) or set appropriately in your environment.
 */

export default function ViewBlogs() {
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: "success", message: "" });

  // edit dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editData, setEditData] = useState({
    id: null,
    title: "",
    categoryId: "",
    authorName: "",
    content: "",
    isPublished: true,
    imagePreview: null,
    imageFile: null,
    existingImageUrl: null,
  });

  const navigate = useNavigate();

  // base url for images (from env or fallback)
  const BASE_API = process.env.REACT_APP_API_URL || window.location.origin;

  const getFullUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    // ensure single slash
    if (url.startsWith("/")) return `${BASE_API}${url}`;
    return `${BASE_API}/${url}`;
  };

  // Load categories + blogs on mount — define async functions inside effect so no missing deps
  useEffect(() => {
    let mounted = true;

    async function loadAll() {
      try {
        setLoading(true);

        // load categories
        try {
          const cRes = await getAllBlogCategoriesAdmin?.();
          if (!mounted) return;
          setCategories(cRes?.data?.data || []);
        } catch (err) {
          console.error("Failed to load categories", err);
          // don't early return — still try to load blogs
        }

        // load blogs
        try {
          const bRes = await getAllBlogsAdmin?.({ limit: 200 });
          if (!mounted) return;
          setBlogs(bRes?.data?.data || []);
        } catch (err) {
          console.error("Failed to fetch blogs", err);
          if (!mounted) return;
          setSnack({ open: true, severity: "error", message: "Failed to load blogs" });
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadAll();

    return () => {
      mounted = false;
    };
  }, []); // no eslint-disable needed

  // STOP PROP helper
  const stop = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
  };

  const handleDelete = async (e, id) => {
    stop(e);
    if (!window.confirm("Are you sure you want to delete this blog?")) return;
    setLoading(true);
    try {
      await deleteBlog(id);
      setSnack({ open: true, severity: "success", message: "Blog deleted successfully" });
      // reload blogs
      const res = await getAllBlogsAdmin?.({ limit: 200 });
      setBlogs(res?.data?.data || []);
    } catch (err) {
      console.error("Delete blog error", err);
      setSnack({ open: true, severity: "error", message: "Failed to delete blog" });
    } finally {
      setLoading(false);
    }
  };

  // Open inline edit dialog
  const openEdit = (e, blog) => {
    stop(e);
    setEditData({
      id: blog.id,
      title: blog.title || "",
      categoryId: blog.categoryId || "",
      authorName: blog.authorName || "",
      content: blog.content || "",
      isPublished: !!blog.isPublished,
      imagePreview: blog.imageUrl ? getFullUrl(blog.imageUrl) : null, // use full url
      existingImageUrl: blog.imageUrl || null,
      imageFile: null,
    });
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditLoading(false);
    setEditData({
      id: null,
      title: "",
      categoryId: "",
      authorName: "",
      content: "",
      isPublished: true,
      imagePreview: null,
      imageFile: null,
      existingImageUrl: null,
    });
  };

  const handleEditChange = (field, value) => {
    setEditData((s) => ({ ...s, [field]: value }));
  };

  const handleImageSelect = (e) => {
    stop(e);
    const file = e.target.files?.[0];
    if (file) {
      handleEditChange("imageFile", file);
      handleEditChange("imagePreview", URL.createObjectURL(file));
    }
  };

  const handleUpdate = async () => {
    // validation
    if (!editData.title.trim() || !editData.categoryId || !editData.content.trim()) {
      setSnack({ open: true, severity: "error", message: "Title, category and content are required" });
      return;
    }

    setEditLoading(true);
    try {
      const fd = new FormData();
      fd.append("title", editData.title.trim());
      fd.append("categoryId", editData.categoryId);
      fd.append("authorName", editData.authorName || "");
      fd.append("content", editData.content);
      fd.append("isPublished", editData.isPublished ? "true" : "false");

      if (editData.imageFile) {
        fd.append("image", editData.imageFile);
      } else {
        if (editData.existingImageUrl) fd.append("imageUrl", editData.existingImageUrl);
      }

      await updateBlog(editData.id, fd);

      setSnack({ open: true, severity: "success", message: "Blog updated successfully" });
      closeEdit();

      // reload blogs
      const res = await getAllBlogsAdmin?.({ limit: 200 });
      setBlogs(res?.data?.data || []);
    } catch (err) {
      console.error("Update blog error", err);
      const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || "Failed to update blog";
      setSnack({ open: true, severity: "error", message: msg });
    } finally {
      setEditLoading(false);
    }
  };

  // View public blog in new tab (open frontend route)
  const handleView = (e, slug) => {
    stop(e);
    // open site public path. If your public blog lives on frontend route /blog/:slug keep this.
    window.open(`/blog/${slug}`, "_blank", "noopener,noreferrer");
  };

  // Row click opens edit modal
  const rowClick = (blog) => {
    setEditData({
      id: blog.id,
      title: blog.title || "",
      categoryId: blog.categoryId || "",
      authorName: blog.authorName || "",
      content: blog.content || "",
      isPublished: !!blog.isPublished,
      imagePreview: blog.imageUrl ? getFullUrl(blog.imageUrl) : null,
      existingImageUrl: blog.imageUrl || null,
      imageFile: null,
    });
    setEditOpen(true);
  };

  return (
    <Box sx={{ pt: { xs: 8, md: 10 }, pb: 6 }}>
      <Container maxWidth="lg">
        <Paper sx={{ p: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.3)" }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">All Blogs</Typography>
            <Button
              variant="contained"
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => navigate("/website-settings/blogs/add")}
              type="button"
            >
              Add Blog
            </Button>
          </Box>

          {loading && <LinearProgress sx={{ mb: 2 }} />}

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Image</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Author</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {blogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No blogs found
                    </TableCell>
                  </TableRow>
                ) : (
                  blogs.map((b) => (
                    <TableRow
                      key={b.id}
                      hover
                      onClick={() => rowClick(b)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>{b.id}</TableCell>

                      <TableCell>
                        {b.imageUrl ? (
                          <Avatar
                            variant="rounded"
                            src={getFullUrl(b.imageUrl)}
                            alt={b.title}
                            sx={{ width: 56, height: 56 }}
                          />
                        ) : (
                          <Avatar variant="rounded" sx={{ width: 56, height: 56 }}>
                            {b.title?.charAt(0)?.toUpperCase() || "B"}
                          </Avatar>
                        )}
                      </TableCell>

                      <TableCell>{b.title}</TableCell>
                      <TableCell>{b.category?.name || "—"}</TableCell>
                      <TableCell>{b.authorName || "—"}</TableCell>
                      <TableCell>
                        <Typography variant="caption" color={b.isPublished ? "success.main" : "error.main"}>
                          {b.isPublished ? "Published" : "Draft"}
                        </Typography>
                      </TableCell>
                      <TableCell>{new Date(b.createdAt).toLocaleDateString("en-IN")}</TableCell>

                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title="View Public Blog">
                            <IconButton size="small" onClick={(e) => handleView(e, b.slug)} type="button">
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Edit Blog">
                            <IconButton size="small" onClick={(e) => openEdit(e, b)} type="button">
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete Blog">
                            <IconButton
                              size="small"
                              onClick={(e) => handleDelete(e, b.id)}
                              color="error"
                              disabled={loading}
                              type="button"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Snackbar
          open={snack.open}
          autoHideDuration={3000}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          <Alert onClose={() => setSnack((s) => ({ ...s, open: false }))} severity={snack.severity} sx={{ width: "100%" }}>
            {snack.message}
          </Alert>
        </Snackbar>
      </Container>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={closeEdit} fullWidth maxWidth="md">
        <DialogTitle>Edit Blog</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label="Title"
              value={editData.title}
              onChange={(e) => handleEditChange("title", e.target.value)}
              fullWidth
              disabled={editLoading}
            />

            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={editData.categoryId}
                label="Category"
                onChange={(e) => handleEditChange("categoryId", e.target.value)}
                disabled={editLoading}
              >
                {categories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Author Name"
              value={editData.authorName}
              onChange={(e) => handleEditChange("authorName", e.target.value)}
              fullWidth
              disabled={editLoading}
            />

            <Box>
              <Typography variant="subtitle2" mb={1}>
                Blog Image
              </Typography>
              <input accept="image/*" type="file" onChange={handleImageSelect} disabled={editLoading} />
              {editData.imagePreview && (
                <Box mt={2}>
                  <img
                    src={editData.imagePreview}
                    alt="preview"
                    style={{ width: "100%", maxHeight: 300, objectFit: "cover", borderRadius: 8 }}
                  />
                </Box>
              )}
            </Box>

            <TextField
              label="Content"
              multiline
              minRows={6}
              value={editData.content}
              onChange={(e) => handleEditChange("content", e.target.value)}
              fullWidth
              disabled={editLoading}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={editData.isPublished}
                  onChange={(e) => handleEditChange("isPublished", e.target.checked)}
                  disabled={editLoading}
                />
              }
              label="Published"
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeEdit} disabled={editLoading} type="button">
            Cancel
          </Button>

          <Button
            onClick={handleUpdate}
            variant="contained"
            disabled={editLoading}
            type="button"
            startIcon={editLoading ? <CircularProgress size={18} /> : null}
          >
            {editLoading ? "Updating..." : "Update Blog"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
